/**
 * RaiseRequestForm component
 *
 * Props:
 * - formSettings: Form settings object containing form field definitions
 * - onClose: Callback function when form is closed
 * - assetId: ID of the asset this request is for
 */

import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import useLabels from '~hooks/useLabels';
import { postRaiseRequestForm } from '~modules/media-library/controller/api/raiseRequestForm';
import { AssetId } from '~modules/media-library/model/Asset';
import { makeFormResponse } from '~modules/media-library/model/RaiseRequestAssets';
import { FieldValue, FormSettings } from '~modules/media-library/model/RaiseRequestFormSettings';
import { FieldGroup } from '~modules/media-library/view/RaiseRequestForm/FieldGroup';
import { StatusActions } from '~store/actions';

type RaiseRequestFormProps = {
  formSettings: FormSettings;
  onClose?: () => void;
  assetId: string;
};

export function RaiseRequestForm({ formSettings, onClose, assetId }: RaiseRequestFormProps) {
  // State for form data values
  const [formData, setFormData] = useState<Record<string, FieldValue>>();

  // Loading state
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Track if form submission is complete
  const [isComplete, setIsComplete] = useState<boolean>(false);

  // Redux dispatch
  const dispatch = useDispatch();

  // Get translated labels
  const {
    mediaLibraryRaiseRequestDescription,
    btnMediaLibraryRaiseRequestClose,
    btnMediaLibraryRaiseRequestOK,
    mediaLibraryRaiseRequestModalHeadline,
  } = useLabels([
    'mediaLibraryRaiseRequestDescription',
    'btnMediaLibraryRaiseRequestClose',
    'btnMediaLibraryRaiseRequestOK',
    'mediaLibraryRaiseRequestModalHeadline',
  ]);

  const useRaiseRequestForm = (assetId: AssetId) => {
    return useMutation({
      mutationFn: async (postData: object) => makeFormResponse(await postRaiseRequestForm(assetId, postData)),
      onError: (err: any) => {
        if (err.status === 500 && err.error) {
          dispatch(StatusActions.setErrorNotification(err.error));
        }
      },
    });
  };

  const { mutate, data } = useRaiseRequestForm(assetId);

  useEffect(() => {
    if (data !== undefined && data.status === 'success' && isComplete === false) {
      setIsComplete(true);
    }
    setIsLoading(false);
  }, [data, isComplete, onClose]);

  useEffect(() => {
    Object.keys(formSettings.fields).forEach(key => {
      setFormData(currentData => {
        return { ...currentData, [formSettings.fields[key].name]: formSettings.fields[key].value };
      });
    });
  }, [formSettings.fields]);

  // Handler to save form changes when submitted
  const onSubmit = useCallback(() => {
    setIsLoading(true);

    // Build FormData from state
    const postData = new FormData();

    Object.keys(formData).forEach(key => {
      if (Array.isArray(formData[key])) {
        // Append each item as separate entries with key[] format
        Object.values(formData[key]).forEach(item => {
          postData.append(String(key) + '[]', String(item));
        });
      } else {
        postData.append(key, String(formData[key]));
      }
    });

    // Submit FormData object to mutate function
    mutate(postData);
  }, [formData, mutate]);

  // Handle value changes
  const onValueChanged = (key: string, value: FieldValue) => {
    setFormData(currentData => {
      return { ...currentData, [key]: value };
    });
  };

  // Show loading state
  if (isLoading) {
    return <div className="loading"></div>;
  }

  // Show success message
  if (isComplete) {
    return (
      <div>
        <header className="ml--detail-modal--panel-header flex pl-3 py-2 pr-8">
          <h2 className="ml--detail-modal--title font-semibold text-base leading-snug overflow-hidden overflow-ellipsis p-0 m-0 h-10 clamp-2">
            {mediaLibraryRaiseRequestModalHeadline}
          </h2>
        </header>
        <p className="pl-3 py-2 pr-8">{mediaLibraryRaiseRequestDescription}</p>
        <div className="flex my-4 justify-center">
          <button className="button c-button c-button--light u-padding-horizontal-lg mx-2" onClick={onClose}>
            {btnMediaLibraryRaiseRequestOK}
          </button>
        </div>
      </div>
    );
  }

  // Render form
  return (
    <>
      <header className="ml--detail-modal--panel-header flex pl-3 py-2 pr-8">
        <h2 className="ml--detail-modal--title font-semibold text-base leading-snug overflow-hidden overflow-ellipsis p-0 m-0 h-10 clamp-2">
          Request
        </h2>
      </header>
      <div className="raise-request-assets-form container py-1 px-3">
        <form>
          {/* Render form fields */}
          {formSettings.fields.map(field => {
            // Check if this field should be hidden based on topic
            const shouldHide =
              field.display_condition &&
              formData['raise_request_form[topic]'] !== field.display_condition.split('=')[1];

            if (shouldHide) {
              return null;
            }

            // Get field error
            const fieldError: string =
              data !== undefined && data.form_errors && data.form_errors[field.key] !== undefined
                ? String(data.form_errors[field.key])
                : '';

            // Get field value
            const fieldValue = formData[field.name] !== undefined ? formData[field.name] : field.value;

            return (
              <FieldGroup
                key={field.key}
                fieldSettings={field}
                fieldValue={fieldValue}
                error={fieldError}
                onValueChanged={onValueChanged}
              />
            );
          })}
        </form>
        <div className="flex my-4 justify-center">
          <button className="button c-button c-button--dark u-padding-horizontal-lg mx-2" onClick={onSubmit}>
            {btnMediaLibraryRaiseRequestOK}
          </button>
          <button className="button c-button c-button--light u-padding-horizontal-lg mx-2" onClick={onClose}>
            {btnMediaLibraryRaiseRequestClose}
          </button>
        </div>
      </div>
    </>
  );
}
